export type ConfigDefinition = {
  key: string;
  safe_default: unknown;
  description?: string;
  risk_tier?: string;
  allowed_product_flows?: string[];
};

export type ConfigRule = {
  id: string;
  key: string;
  priority: number;
  effect: "deny" | "grant" | "percentage";
  value?: boolean;
  product_flows?: string[];
  basis_points?: number;
  salt?: string;
};

export type ConfigRelease = {
  id: string;
  sequence: number;
  digest: string;
  values: Record<string, unknown>;
  rules: ConfigRule[];
  reason: string;
};

export type ConfigReadback = {
  environment?: "staging" | "stg";
  service?: string;
  service_identity?: string;
  instance_id: string;
  release_id: string;
  digest: string;
  validation_result: string;
  updated_at?: string;
  applied_at?: string;
  provider_experiment_contracts?: string[];
};

export type ConfigDraft = {
  id: string;
  version: number;
  status: "draft" | "validated" | "approved";
  created_by: string;
  approved_by?: string | null;
  reason: string;
  ticket_reference?: string | null;
};

export type ControlPlanePayload = {
  environment: "staging" | "stg";
  product_meaning: "engineering staging" | "customer-facing production";
  definitions: ConfigDefinition[];
  active_release: ConfigRelease | null;
  pending_drafts: ConfigDraft[];
  readbacks: ConfigReadback[];
};

export type ControlPlaneState =
  | { status: "ok"; payload: ControlPlanePayload }
  | { status: "unconfigured" }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

export type CreateConfigDraftInput = {
  base_release_id: string | null;
  values: Record<string, unknown>;
  rules: ConfigRule[];
  reason: string;
  ticket_reference: string;
};

export type RebaseSoundDraftResult =
  | {
      status: "validated";
      replaced_draft_id: string;
      draft: { id: string; version: number; status: "validated" };
    }
  | {
      status: "already_applied";
      expired_draft_id: string;
      active_release_id: string;
    };

export type RuntimeConfigErrorCode =
  | "unauthorized"
  | "forbidden"
  | "conflict"
  | "invalid"
  | "rate_limited"
  | "dependency_unavailable"
  | "unconfigured"
  | "unknown";

export class RuntimeConfigRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: RuntimeConfigErrorCode,
    message: string,
    readonly retryAfterSeconds: number | null = null,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "RuntimeConfigRequestError";
  }
}
